/**
 * Abertura de convite por link.
 *
 * Rota pública: quem clica no link do WhatsApp pode nem ter conta. A prévia
 * mostra só o que qualquer convidado legítimo já veria — nome, dia, local — e
 * leva ao cadastro (ou login) quando não há sessão. O código fica pendente até a
 * pessoa se autenticar; ao voltar para cá, o aceite acontece sozinho.
 */

import type { InvitePreview } from '@nossorachao/types';
import { weekdayLabel } from '@nossorachao/utils';
import { Calendar, MapPin, Users, Volleyball as Ball, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, EmptyState, Skeleton } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { clearPendingInvite, getPendingInvite, setPendingInvite } from '@/lib/pending-invite';
import { useAcceptInvite } from '@/lib/queries';
import { useAuth } from '@/store/auth';

const REASONS: Record<string, string> = {
  EXPIRED: 'Este convite expirou. Peça um link novo para quem te chamou.',
  REVOKED: 'Este convite foi cancelado pelo administrador.',
  MAX_USES_REACHED: 'Este convite já atingiu o limite de usos.',
  GROUP_CLOSED: 'Este grupo não está aceitando novos participantes no momento.',
  NOT_FOUND: 'Convite não encontrado. Confira se o link está completo.',
};

export function InvitePage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();

  const isAuthenticated = useAuth((state) => state.isAuthenticated);
  const accept = useAcceptInvite();

  const preview = useQuery({
    queryKey: ['invite', code],
    queryFn: () => api.get<InvitePreview>(`/invites/${code}`, { skipAuth: true }),
    enabled: Boolean(code),
    retry: false,
  });

  async function handleAccept() {
    try {
      const result = await accept.mutateAsync(code);
      navigate(result.pendingApproval ? '/grupos' : `/grupos/${result.groupId}`, {
        replace: true,
      });
    } catch (caught) {
      // Quem já está no grupo não tem o que aceitar: vai para a lista em vez de
      // ver um erro. Os demais aparecem abaixo do botão.
      if (caught instanceof ApiError && caught.code === 'ALREADY_MEMBER') {
        navigate('/grupos', { replace: true });
      }
    } finally {
      // Só agora, com a resposta em mãos. Apagar antes abria uma corrida com a
      // tela de cadastro, que também redireciona para o convite pendente: sem
      // ele, ela mandava para o início e a pessoa nunca entrava na pelada.
      clearPendingInvite();
    }
  }

  function goToAuth(path: '/criar-conta' | '/entrar') {
    setPendingInvite(code);
    navigate(path);
  }

  // Voltou autenticado de login/cadastro iniciado aqui: conclui a entrada sem
  // pedir outro clique. O ref segura o duplo efeito do StrictMode.
  const autoAccepted = useRef(false);
  const cameFromAuth = isAuthenticated && getPendingInvite() === code;

  useEffect(() => {
    if (!cameFromAuth || preview.isLoading || autoAccepted.current) return;

    autoAccepted.current = true;
    if (preview.data?.valid) void handleAccept();
    // Convite vencido não pode ficar guardado: todo login futuro cairia aqui.
    else clearPendingInvite();
  }, [cameFromAuth, preview.isLoading, preview.data?.valid]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-md">
        {preview.isLoading ? (
          <Card>
            <Skeleton className="mx-auto h-16 w-16 rounded-full" />
            <Skeleton className="mx-auto mt-4 h-6 w-48" />
            <Skeleton className="mt-6 h-24 w-full" />
          </Card>
        ) : !preview.data?.valid ? (
          <Card>
            <EmptyState
              icon={XCircle}
              title="Convite indisponível"
              description={REASONS[preview.data?.reason ?? 'NOT_FOUND'] ?? REASONS.NOT_FOUND}
              action={
                <Button variant="secondary" onClick={() => navigate('/')}>
                  Ir para o início
                </Button>
              }
            />
          </Card>
        ) : (
          <Card>
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand">
                <Ball className="h-9 w-9 text-on-brand" aria-hidden />
              </span>

              <p className="text-sm text-ink-2">Você foi convidado para participar da</p>
              <h1 className="text-2xl font-extrabold tracking-tight">{preview.data.groupName}</h1>
            </div>

            <div className="mt-6 space-y-2.5 rounded-lg bg-sunken p-4 text-sm text-ink-2">
              <p className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-ink-3" aria-hidden />
                {weekdayLabel(preview.data.weekday)} às {preview.data.startTime}
              </p>

              {preview.data.venueName ? (
                <p className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-ink-3" aria-hidden />
                  {preview.data.venueName}
                </p>
              ) : null}

              <p className="flex items-center gap-2">
                <Users className="h-4 w-4 text-ink-3" aria-hidden />
                {preview.data.memberCount} participantes
              </p>
            </div>

            {isAuthenticated ? (
              <Button
                size="lg"
                full
                className="mt-6"
                loading={accept.isPending}
                onClick={() => void handleAccept()}
              >
                Entrar na pelada
              </Button>
            ) : (
              <div className="mt-6 space-y-2">
                <Button size="lg" full onClick={() => goToAuth('/criar-conta')}>
                  Criar conta e entrar
                </Button>
                <Button variant="ghost" size="lg" full onClick={() => goToAuth('/entrar')}>
                  Já tenho conta
                </Button>
              </div>
            )}

            {accept.error ? (
              <p className="mt-3 text-center text-[13px] text-bad">
                {accept.error instanceof Error ? accept.error.message : 'Não foi possível entrar.'}
              </p>
            ) : null}
          </Card>
        )}
      </div>
    </div>
  );
}
